import { AxiosError, AxiosResponse } from 'axios'
import { snakeCase, camelCase } from 'change-case'
import { handleUnauthorized } from 'src/services/auth/unauthorized'
import { notify } from 'src/components/ui/notify'
import i18n from 'src/locale/i18n'

declare module 'axios' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  export interface AxiosRequestConfig<D = any> {
    // The caller shows its own "no access" state, the generic 403 toast is not needed
    skipForbiddenToast?: boolean
  }
}

export interface INormalizeResponse<Data> extends AxiosResponse<Data> {
  nextCursor: string | null
  previousCursor: string | null
  meta: {
    cursor?: string
    prevCursor: string
    nextCursor: string
    currentPage: number
    lastPage: number
    total?: number
  }
  token: string
  status: number
}

type Response = INormalizeResponse<{
  data: unknown
  message: string
  nextCursor: string | null
  previousCursor: string | null
  meta: {
    cursor?: string
    prevCursor: string
    nextCursor: string
    currentPage: number
    lastPage: number
  }
  token: string
  status: number
}>

export const NormalizeInterceptorResponseFullfiled = (
  config: AxiosResponse
) => {
  if (config.data) {
    const { data, message, nextCursor, previousCursor, meta, token } =
      config.data as Response['data']
    if (data) {
      config.data = data
    }
    config.statusText = message
    ;(config as INormalizeResponse<unknown>).status = config.status
    ;(config as INormalizeResponse<unknown>).token = token
    ;(config as INormalizeResponse<unknown>).meta = meta || {}
    ;(config as INormalizeResponse<unknown>).nextCursor = nextCursor
    ;(config as INormalizeResponse<unknown>).previousCursor = previousCursor
  }

  return config
}

const AUTH_REDIRECT_SKIP_URLS = [
  '/user/login',
  '/user/reset-password',
  '/partner-check',
  '/oauth/token',
]

const shouldSkipAuthRedirect = (error: AxiosError) => {
  const requestUrl = error.config?.url || error.response?.config?.url || ''

  return AUTH_REDIRECT_SKIP_URLS.some((path) => requestUrl.includes(path))
}

const FORBIDDEN_TOAST_ID = 'forbidden'
const forbiddenNotifiedErrors = new WeakSet<AxiosError>()

const notifyForbidden = (error: AxiosError) => {
  if (error.config?.skipForbiddenToast) return
  // The login / password / oauth forms show the answer of the API themselves
  if (shouldSkipAuthRedirect(error)) return
  // The same error can pass through several interceptors: one toast per request
  if (forbiddenNotifiedErrors.has(error)) return

  forbiddenNotifiedErrors.add(error)

  // The constant id keeps a single toast when several requests of one page are denied
  notify({
    id: FORBIDDEN_TOAST_ID,
    type: 'error',
    title: i18n.t('common:accessDenied'),
    description: i18n.t('common:noPermissionForAction'),
  })
}

export const ErrorProcessing = (error: AxiosError) => {
  if (error.response?.status === 401 && !shouldSkipAuthRedirect(error)) {
    handleUnauthorized()
  }

  if (error.response?.status === 403) {
    notifyForbidden(error)
  }

  if (error.response && isValidationErrors(error.response)) {
    const validationErrors = getValidationErrors(
      error.response as AxiosResponse<IErrorValidationData>
    )
    ;(
      error.response.data as
        | IValidationErrorsData
        | IValidationMultiplyErrorsData
    ).validationErrors = validationErrors
  }

  return Promise.reject(error)
}

export interface IValidationErrorsData extends IErrorValidationData {
  validationErrors: TValidationErrors
}

export interface IValidationMultiplyErrorsData extends IErrorValidationData {
  validationErrors: TValidationMultiplyErrors
}

export interface IFieldValidationError {
  identifier: string
  messages: string[]
}
export type TValidationErrors = IFieldValidationError[]
export type TValidationMultiplyErrors = IFieldValidationError[][]

interface IErrorValidationData {
  errors: {
    [key: string]: string[]
  }
}

export const isValidationErrors = (response: AxiosResponse<unknown>) => {
  return response.status === 422
}
export const getValidationErrors = (
  response: AxiosResponse<IErrorValidationData>
) => {
  const errors = response?.data?.errors || {}
  const identifiers = Object.keys(errors)

  const isMultiplieFieldsValidation = Boolean(
    identifiers.find((identifier) => {
      const [index] = snakeCase(identifier).split('_')

      return !isNaN(Number(index))
    })
  )

  if (isMultiplieFieldsValidation) {
    const filedErrors = identifiers.reduce((prev, key) => {
      const messages = errors[key]
      const [index] = snakeCase(key).split('_')
      const identifier = camelCase(key.replace(index, ''))

      if (!Array.isArray(prev[Number(index)])) {
        prev[Number(index)] = []
      }
      prev[Number(index)].push({
        identifier,
        messages: messages.map((message) =>
          message.replace(`${index}.${snakeCase(identifier)}`, identifier)
        ),
      })

      return prev
    }, [] as TValidationMultiplyErrors)

    return filedErrors
  } else {
    const filedErrors = identifiers.reduce((prev, identifier) => {
      const messages = errors[identifier]
      prev.push({
        identifier,
        messages,
      })

      return prev
    }, [] as TValidationErrors)

    return filedErrors
  }
}
